import { Ellipsis } from '@/components/ui/ellipsis';
import { cn } from '@/utils/classUtils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  createContext,
  useContext,
  type ButtonHTMLAttributes,
  type FC,
  type Ref,
} from 'react';

export interface ChipProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof chipVariants> {
  asChild?: boolean;
  interactive?: boolean;
}

export const chipVariants = cva(
  'inline-flex items-center justify-center' +
    'box-border border border-neutral-300 transition-colors ' +
    'rounded-full h-fit shadow-xs bg-white ',
  {
    variants: {
      interactive: {
        true:
          'hover:bg-neutral-50 hover:active:bg-neutral-100 ' +
          'focus-visible:border-green focus-visible:bg-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-green ' +
          'disabled:pointer-events-none disabled:text-disabled disabled:bg-neutral-50 disabled:border-tertiary ' +
          'text-sm',
        false: 'cursor-default',
      },
      size: {
        sm: 'px-2 py-0.5 gap-1 leading-none min-h-5 min-w-5',
        md: 'px-2.5 py-1.5 gap-1.5 min-h-5 min-w-5',
      },

      active: {
        true: 'bg-green-50 hover:bg-green-50 hover:active:bg-green-100 focus:bg-green-50 text-green focus:text-green-600 hover:text-green-700 hover:active:text-green-700',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

type ChipContextType = VariantProps<typeof chipVariants>;
const chipContext = createContext<ChipContextType | null>(null);
type ChipComponentType = FC<ChipProps & { ref?: Ref<HTMLButtonElement> }> & {
  Label: typeof ChipLabel;
  Value: typeof ChipValue;
};

const ChipValue: FC<{ ref?: Ref<HTMLDivElement> }> = ({ ref, ...props }) => {
  const ctx = useContext(chipContext);

  if (!ctx) {
    throw new Error('Chip.Value must be used within a Chip');
  }

  return (
    <Ellipsis
      className="max-w-[20ch]"
      asChild
      hasTooltip
      tooltipSideOffset={ctx.size === 'sm' ? 5 : 10}
    >
      <p
        className={ctx.active ? 'text-body-sm-bold' : 'text-body-sm'}
        {...props}
        ref={ref}
      />
    </Ellipsis>
  );
};

const ChipLabel: FC<{
  ref?: Ref<HTMLDivElement>;
  className?: string;
  children?: React.ReactNode;
}> = ({ ref, className, children, ...props }) => {
  const ctx = useContext(chipContext);

  if (!ctx) {
    throw new Error('Chip.Label must be used within a Chip');
  }

  const separator = ctx.active ? ' : ' : '';

  return (
    <p className={cn('text-sm', className)} {...props} ref={ref}>
      {children}
      {separator}
    </p>
  );
};

export const Chip: ChipComponentType = ({
  active: propsActive,
  className,
  size,
  asChild = false,
  children,
  interactive = true,
  ref,
  ...props
}) => {
  const active = interactive ? propsActive : false;
  const Comp = asChild ? Slot : 'button';
  return (
    <chipContext.Provider value={{ active, size }}>
      <Comp
        className={cn(
          chipVariants({
            size,
            active,
            interactive,
            className,
          })
        )}
        ref={ref}
        {...props}
        disabled={props.disabled}
      >
        {children}
      </Comp>
    </chipContext.Provider>
  );
};

Chip.Value = ChipValue;
Chip.Label = ChipLabel;
